import { arraysEqual } from '../utilities.js';
import { PRODUCT_LINE_MAPPING, PRODUCT_LINE_FIELDS } from '../constants/product_line';

/**
 * Given a hierarchical product line object, will flatten the structure into a
 * map of product lines Ids and the corresponding product line. The provided
 * accumulator will be populated with this map data.
 * @param {*} rootProductLine The root product line object.
 * @param {*} parentItem The parent of the root product line item.
 * @param {*} accumulator The accumulator where the map structure is to be saved.
 */
export const accumulateProductLineById = (rootProductLine, parentItem, accumulator) => {
  let productLine = rootProductLine;
  accumulator[productLine.id] = { ...productLine, parentItem };
  for (let child of productLine.children || []) {
    accumulateProductLineById(child, productLine, accumulator);
  }
};

/**
 * Checks if a GL item is considered to be 'Unmapped'
 * @param {*} gls The list of gl Ids.
 * @param {*} ignored The corresponding ignored flag
 */
export const isUnmappedGl = (gls , ignored) => {
  return (!gls || !gls.length || (gls.length === 1 && gls[0] === -1)) && !ignored;
};

/**
 * Compares a product line object with another and checks if the
 * original product line object has been modified.
 *
 * @param {*} originalProductLine The original product line.
 * @param {*} modifiedProductLine The modified product line.
 * @param {*} fieldsToCheck The list of fields to check for modification.
 */
export const isProductLineModified = (
  originalProductLine,
  modifiedProductLine,
  fieldsToCheck = PRODUCT_LINE_MAPPING.EDITABLE_FIELDS
) => {
  // Checking a fixed no of fields isn't generic - but
  // more efficient than doing a deep comparison.
  for (let field of fieldsToCheck) {
    const originalValue = originalProductLine[field];
    const modifiedValue = modifiedProductLine[field];

    // Special handing for field 'gls'. A value of 'null', '[]' or '[-1]' indicates that field 'gls' is unmapped.
    // So we consider them as equivalent and continue to the next field comparison.
    if (field === PRODUCT_LINE_FIELDS.GLS && isUnmappedGl(originalValue, false) && isUnmappedGl(modifiedValue, false)) {
      continue;
    }

    if (!areFieldValuesEquivalent(originalValue, modifiedValue)) {
      return true;
    }
  }

  return false;
};

/**
 * Checks if two values of product lines fields
 * can be considered to be equivalent.
 * If the field value is an object with an 'id' property,
 * the equivalence of this 'id' will be compared.
 */
export const areFieldValuesEquivalent = (value1, value2) => {
  if (!value1 && !value2) {
    return true;
  }

  if ((!value1 && value2) || (value1 && !value2)) {
    return false;
  }

  if (Array.isArray(value1) && Array.isArray(value2)) {
    return arraysEqual(value1, value2);
  }

  if (value1.hasOwnProperty('id') && value2.hasOwnProperty('id')) {
    return value1['id'] === value2['id'];
  }

  return value1 === value2;
};



// WEBPACK FOOTER //
// ./src/utils/product_line.jsx